// /trial — the free-trial gate (Jumper-style split card, ClipRadar dress).
// Left: email → POST /api/trial (Keygen trial license + download link by email).
// Right: one strong tester quote. Standalone page: just a Back button, no nav.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CRWIcon } from '../components/icons';
import { CRWLogo, MetalBtn, MonoNote } from '../components/primitives';
import { STILLS } from '../components/stills';
import { usePageTitle } from './shell';
import { getAttribution } from '../track';
import { phCapture, phIdentify } from '../posthog';

const inputStyle = {
  width: '100%', boxSizing: 'border-box' as const, fontFamily: 'var(--font-mono)', fontSize: 14, color: 'var(--fg-1)',
  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 10,
  padding: '13px 14px', boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.4)',
};

// verbatim from the tester pool (social.tsx QUOTES) — never invent quotes
const QUOTE = {
  q: 'I pull b-roll from a massive archive for every video. I describe the shot I’ve got in my head and it surfaces — no more scrubbing folders by filename.',
  n: 'Martin K.',
  r: 'Long-form YouTube editor',
};

export function TrialPage() {
  usePageTitle('Start your free trial');
  const [email, setEmail] = useState('');
  const [trap, setTrap] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);

  const start = async () => {
    if (state === 'sending') return;
    const value = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) { setError('That doesn’t look like an email — check it and try again.'); return; }
    setError(null); setState('sending');
    try {
      const attr = getAttribution();
      const r = await fetch('/api/trial', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: value, company: trap, ...(attr ? { attr } : {}) }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(d.error || 'That didn’t work — try again in a minute.'); setState('idle'); return; }
      setState('done');
      phIdentify(value); // they just gave us their email — name the journey
      phCapture('trial_requested', { src: attr?.src ?? null });
    } catch {
      setError('That didn’t work — try again in a minute.');
      setState('idle');
    }
  };

  return (
    <div style={{ minHeight: '100vh', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* atmosphere — ONE light: a low glacier horizon behind the card. The
          grid reads as the floor converging toward it; two static hairline
          rings rise from the same point (quiet radar echo, no sweep). */}
      <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'var(--bg-grid)', maskImage: 'radial-gradient(ellipse 75% 55% at 50% 82%, black 20%, transparent 72%)', WebkitMaskImage: 'radial-gradient(ellipse 75% 55% at 50% 82%, black 20%, transparent 72%)', opacity: 0.5 }}></div>
        {/* the bloom */}
        <div style={{ position: 'absolute', left: '50%', bottom: '-4%', transform: 'translateX(-50%)', width: 1400, height: 560, background: 'radial-gradient(ellipse 50% 100% at 50% 100%, rgba(var(--light-rgb),0.2), rgba(var(--light-deep-rgb),0.08) 45%, transparent 72%)', filter: 'blur(28px)' }}></div>
        {/* the light's edge — a crisp horizon line just behind the card's lower third */}
        <div style={{ position: 'absolute', left: '50%', bottom: '17%', transform: 'translateX(-50%)', width: 'min(1120px, 86vw)', height: 1, background: 'linear-gradient(90deg, transparent, rgba(var(--light-high-rgb),0.4) 30%, rgba(var(--light-high-rgb),0.55) 50%, rgba(var(--light-high-rgb),0.4) 70%, transparent)' }}></div>
        {/* two static rings rising from the horizon center */}
        {[520, 940].map((d) => (
          <div key={d} style={{ position: 'absolute', left: '50%', bottom: '17%', transform: 'translate(-50%, 50%)', width: d, height: d, borderRadius: '50%', border: '1px solid rgba(var(--light-high-rgb),0.1)', maskImage: 'linear-gradient(180deg, black 50%, transparent 50%)', WebkitMaskImage: 'linear-gradient(180deg, black 50%, transparent 50%)' }}></div>
        ))}
      </div>

      {/* back */}
      <div style={{ position: 'relative', padding: '18px 22px' }}>
        <Link to="/" className="crw-glass" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 16px 9px 12px', borderRadius: 11, textDecoration: 'none', fontSize: 13, fontWeight: 500, color: 'var(--fg-1)' }}>
          <CRWIcon d="arrowRight" size={13} style={{ transform: 'rotate(180deg)' }} /> Back
        </Link>
      </div>

      <main style={{ position: 'relative', flex: 1, display: 'grid', placeItems: 'center', padding: '24px 24px 80px' }}>
        {/* gradient ring — brightest at the BOTTOM edge, where the horizon
            light actually hits the card */}
        <div className="crw-page-in" style={{ width: '100%', maxWidth: 942, borderRadius: 19, padding: 1, background: 'linear-gradient(350deg, rgba(var(--light-high-rgb),0.4), rgba(255,255,255,0.07) 40%, rgba(255,255,255,0.12) 96%)', boxShadow: '0 40px 100px rgba(0,0,0,0.6), 0 24px 90px rgba(var(--light-rgb),0.1)' }}>
        <div className="crw-trial-card" style={{ display: 'grid', gridTemplateColumns: '1.05fr 1fr', borderRadius: 18, overflow: 'hidden' }}>

          {/* ---- left: the form. One continuous stack, vertically centered —
               the right panel drives the card height, so centering spreads the
               slack symmetrically instead of pooling it above floor-anchored
               chips (the old marginTop:auto dead-space bug). ---- */}
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 18, padding: '40px 38px', background: 'linear-gradient(180deg, rgba(16,16,20,0.94), rgba(10,10,13,0.96))', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
            <CRWLogo size={24} />
            <h1 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 'clamp(24px, 3.2vw, 30px)', fontWeight: 700, letterSpacing: 'var(--tracking-display)', lineHeight: 1.15, color: 'var(--fg-1)' }}>
              Start finding your footage.
            </h1>

            {state === 'done' ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--positive)' }}>
                  <CRWIcon d="check" size={18} sw={2.4} />
                  <b style={{ fontSize: 15.5, color: 'var(--fg-1)' }}>Check your inbox.</b>
                </div>
                <p style={{ margin: 0, fontSize: 14, lineHeight: 1.7, color: 'var(--fg-2)' }}>
                  Your trial license key and the download link are on their way to{' '}
                  <b style={{ color: 'var(--fg-1)', overflowWrap: 'anywhere' }}>{email.trim()}</b>. No email after a couple of
                  minutes? Check spam — or your key is always at{' '}
                  <Link to="/account" style={{ color: 'var(--signal-400)', textDecoration: 'none' }}>getclipradar.com/account</Link>.
                </p>
                <Link to="/download" style={{ textDecoration: 'none', display: 'flex' }}>
                  <MetalBtn style={{ width: '100%' }}>
                    <CRWIcon d="download" size={14} style={{ marginRight: 8, verticalAlign: -2 }} />
                    Download ClipRadar
                  </MetalBtn>
                </Link>
              </>
            ) : (
              <>
                <p style={{ margin: 0, fontSize: 14, lineHeight: 1.7, color: 'var(--fg-2)' }}>
                  We’ll email you the download link and your free trial license key.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <input
                    type="email" value={email} placeholder="name@studio.com" autoComplete="email" aria-label="Email address"
                    aria-invalid={Boolean(error)}
                    onChange={(e) => { setEmail(e.target.value); setError(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') void start(); }}
                    className="crw-input"
                    style={inputStyle}
                  />
                  {/* honeypot — invisible to humans, bots fill it */}
                  <input value={trap} onChange={(e) => setTrap(e.target.value)} name="company" tabIndex={-1} autoComplete="off" aria-hidden="true"
                    style={{ position: 'absolute', left: -9999, width: 1, height: 1, opacity: 0, pointerEvents: 'none' }} />
                  {/* error sits under the field it describes; slot always reserved
                      so a failed submit never grows the card (ui-critic) */}
                  <span role="alert" aria-live="polite" style={{ minHeight: 21, marginTop: -4, fontFamily: 'var(--font-mono)', fontSize: 13, letterSpacing: '0.02em', lineHeight: 1.6, color: '#f87171' }}>{error ?? ''}</span>
                  <MetalBtn onClick={() => void start()} busy={state === 'sending'} style={{ width: '100%' }}>
                    {state === 'sending' ? 'Setting up your trial…' : 'Start Free Trial'}
                  </MetalBtn>
                  {/* the reassurance lives WITH the action, not at the card's floor */}
                  <MonoNote style={{ textTransform: 'none', letterSpacing: '0.04em', marginTop: 2 }}>40 free searches · No credit card required</MonoNote>
                </div>
              </>
            )}

            {/* requirements = the fine print of the decision — joined to the
                action cluster behind one quiet rule, never floor-anchored */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, borderTop: '1px solid var(--line-1)', paddingTop: 16 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {([['monitor', 'Windows 10/11'], ['cpu', 'NVIDIA GPU (CUDA)']] as const).map(([ic, label]) => (
                  <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '5px 11px', borderRadius: 999, fontSize: 11.5, color: 'var(--fg-3)', border: '1px solid var(--line-2)', background: 'rgba(255,255,255,0.02)' }}>
                    <span style={{ display: 'inline-flex', color: 'var(--signal-400)' }}><CRWIcon d={ic} size={12} /></span>{label}
                  </span>
                ))}
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '5px 11px', borderRadius: 999, fontSize: 11.5, color: 'var(--fg-3)', border: '1px solid var(--line-2)', background: 'rgba(255,255,255,0.02)' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 15, height: 15, borderRadius: 4, fontFamily: 'var(--font-display)', fontSize: 8.5, fontWeight: 700, color: '#EA77FF', background: '#1E0B2E', border: '1px solid rgba(234,119,255,0.35)' }}>Pr</span>
                  Works with Premiere Pro
                </span>
              </div>
            </div>
          </div>

          {/* ---- right: the product's face + one tester voice. Glimpse first
               (what you're getting), the quote under it (why it's worth it). ---- */}
          <div className="crw-trial-quote" style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 26, padding: '38px 36px', textAlign: 'center', borderLeft: '1px solid var(--line-1)', background: 'linear-gradient(200deg, rgba(13,13,17,0.9), rgba(8,8,10,0.94))' }}>
            <span aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 70% 50% at 50% 88%, rgba(var(--light-rgb),0.06), transparent 70%)', pointerEvents: 'none' }}></span>
            {/* the glimpse — a working search, honest app palette */}
            <div style={{ position: 'relative', width: '100%', maxWidth: 340, borderRadius: 10, background: '#101013', border: '1px solid var(--line-2)', padding: 10, display: 'flex', flexDirection: 'column', gap: 8, textAlign: 'left' }}>
              <div style={{ borderRadius: 7, background: '#17171B', border: '1px solid rgba(59,130,246,0.45)', boxShadow: '0 0 0 2px rgba(59,130,246,0.10)', padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: 5 }}>
                <span style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 4, padding: '1px 7px', borderRadius: 5, fontSize: 8.5, color: 'var(--fg-2)', background: 'rgba(255,255,255,0.05)', border: '1px dashed rgba(255,255,255,0.14)' }}>@ All analyzed media</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ flex: 1, fontSize: 11, color: 'var(--fg-1)' }}>the flag planted on the moon</span>
                  <span style={{ width: 20, height: 17, flex: 'none', borderRadius: 5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'var(--app-blue)', color: '#F2F9FF' }}><CRWIcon d="arrowRight" size={10} sw={2.4} /></span>
                </span>
              </div>
              {/* three DIFFERENT moments answering one visual query (never three
                  crops of the same frame — that undersells the search). Archival
                  demo library (founder, 2026-07-17) — results verify themselves. */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                {([
                  { img: STILLS.flagplant, pos: '50% 40%', tc: '00:00:31 - 00:00:44' },
                  { img: STILLS.moonwalkers, pos: '50% 40%', tc: '00:00:52 - 00:01:03' },
                  { img: STILLS.moonflag, pos: '50% 40%', tc: '00:01:05 - 00:01:14' },
                ] as const).map((c) => (
                  <div key={c.tc}>
                    <div style={{ position: 'relative', aspectRatio: '16 / 9', borderRadius: 5, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }}>
                      <img src={c.img} alt="" loading="lazy" decoding="async" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: c.pos }} />
                    </div>
                    <span style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 7.5, color: 'var(--app-blue-text)', marginTop: 3 }}>{c.tc}</span>
                  </div>
                ))}
              </div>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 7.5, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--fg-3)' }}>3 matches · 0.34s · your drives, not the cloud</span>
            </div>
            <span style={{ position: 'relative', fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--fg-4)' }}>From the closed beta</span>
            <div className="crw-qb-wrap" style={{ maxWidth: 400 }}>
              <span className="crw-qb-tick crw-tin" aria-hidden="true"><i>IN</i></span>
              <blockquote style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 18.5, fontWeight: 600, lineHeight: 1.55, letterSpacing: '-0.01em', color: 'var(--fg-1)', textWrap: 'pretty' }}>
                “{QUOTE.q}”
              </blockquote>
              <span className="crw-qb-tick crw-tout" aria-hidden="true"><i>OUT</i></span>
            </div>
            <figcaption style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className="crw-qb-disc">{QUOTE.n.split(' ').map((w) => w[0]).join('')}</span>
              <b style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-1)' }}>{QUOTE.n}</b>
              <i style={{ fontStyle: 'normal', fontSize: 12.5, color: 'var(--fg-3)' }}>· {QUOTE.r}</i>
            </figcaption>
          </div>
        </div>
        </div>
      </main>
    </div>
  );
}
